"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProvisionContentPage = exports.ProvisionContentPagePrice = exports.ProvisionContentPageItem = exports.ProvisionContentManifest = exports.ProvisionContentPageQuery = exports.PROVISION_CONTENT_PAGE_MAX_BARCODES = exports.PROVISION_CONTENT_PAGE_MAX_PRICES = exports.PROVISION_CONTENT_PAGE_MAX_LIMIT = exports.PROVISION_CONTENT_PAGE_DEFAULT_LIMIT = exports.PROVISION_CONTENT_PAGED_CONTRACT = exports.ProvisionTerminalApplications = exports.ProvisionTerminalApplication = exports.ProvisionTerminalApplicationBinding = exports.ProvisionObservedStateReport = exports.ProvisionObservedDeviceHealth = exports.ProvisionRolloutStateAck = exports.ProvisionRolloutStateReport = exports.ProvisionArtifactInstallResult = exports.ProvisionArtifactDownloadGrant = exports.ProvisionRolloutWork = exports.ProvisionArtifactPair = exports.ProvisionArtifactDescriptor = exports.ProvisionDesiredState = exports.ProvisionUpdateState = void 0;
const zod_1 = require("zod");
const terminal_js_1 = require("./terminal.js");
exports.ProvisionUpdateState = zod_1.z.enum([
    'pending',
    'offline_pending',
    'queued',
    'downloading',
    'downloaded',
    'waiting_for_idle',
    'installing',
    'restarting',
    'verifying',
    'succeeded',
    'failed',
    'cancelled',
    'superseded',
]);
exports.ProvisionDesiredState = zod_1.z
    .object({
    applicationInstanceId: zod_1.z.string().min(1),
    revision: zod_1.z.number().int().positive(),
    checksum: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    strictestActivationPolicy: zod_1.z.enum(['live', 'when_idle', 'restart_required']),
    desiredState: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    sourceChain: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    createdAt: zod_1.z.string().datetime(),
})
    .strict();
exports.ProvisionArtifactDescriptor = zod_1.z
    .object({
    artifactId: zod_1.z.string().min(1),
    kind: zod_1.z.enum(['core', 'ui']),
    semanticVersion: zod_1.z.string().min(1),
    platform: zod_1.z.string().min(1),
    architecture: zod_1.z.string().min(1),
    sizeBytes: zod_1.z.string().regex(/^\d+$/),
    sha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    signature: zod_1.z.string().min(1),
    signingKeyId: zod_1.z.string().min(1),
    uiPortVersion: zod_1.z.string().min(1).nullable(),
    coreVersionRange: zod_1.z.string().min(1).nullable(),
    uiPortVersionRange: zod_1.z.string().min(1).nullable(),
})
    .strict();
exports.ProvisionArtifactPair = zod_1.z
    .object({
    core: exports.ProvisionArtifactDescriptor,
    ui: exports.ProvisionArtifactDescriptor,
})
    .strict()
    .superRefine((pair, context) => {
    if (pair.core.kind !== 'core' || pair.ui.kind !== 'ui') {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Desired artifact pair must contain core and UI in their named slots',
        });
    }
});
exports.ProvisionRolloutWork = zod_1.z
    .object({
    rolloutTargetId: zod_1.z.string().min(1),
    rolloutId: zod_1.z.string().min(1),
    applicationInstanceId: zod_1.z.string().min(1),
    attempt: zod_1.z.number().int().positive(),
    leaseExpiresAt: zod_1.z.string().datetime(),
    state: exports.ProvisionUpdateState,
    installAuthorized: zod_1.z.boolean(),
    desired: exports.ProvisionDesiredState,
    desiredArtifacts: exports.ProvisionArtifactPair,
    // Only artifacts whose active identity differs from the desired pair are
    // delivered. Legacy clients with no observed identities receive both.
    artifacts: zod_1.z.array(exports.ProvisionArtifactDescriptor).min(1).max(2),
})
    .strict();
exports.ProvisionArtifactDownloadGrant = zod_1.z
    .object({
    grantId: zod_1.z.string().min(1),
    rolloutTargetId: zod_1.z.string().min(1),
    applicationInstanceId: zod_1.z.string().min(1),
    artifact: exports.ProvisionArtifactDescriptor,
    url: zod_1.z.string().url(),
    expiresAt: zod_1.z.string().datetime(),
})
    .strict();
exports.ProvisionArtifactInstallResult = zod_1.z
    .object({
    artifactId: zod_1.z.string().min(1),
    kind: zod_1.z.enum(['core', 'ui']),
    state: zod_1.z.enum(['downloaded', 'installed', 'activated', 'failed']),
    integrityVerified: zod_1.z.boolean(),
    errorCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable()
        .optional(),
})
    .strict();
exports.ProvisionRolloutStateReport = zod_1.z
    .object({
    clientEventId: zod_1.z.string().uuid(),
    attempt: zod_1.z.number().int().positive(),
    state: exports.ProvisionUpdateState,
    integrityVerified: zod_1.z.boolean().optional(),
    artifactResults: zod_1.z.array(exports.ProvisionArtifactInstallResult).max(2).optional(),
    errorCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable()
        .optional(),
    reportedAt: zod_1.z.string().datetime(),
})
    .strict()
    .superRefine((report, context) => {
    if (report.integrityVerified !== undefined &&
        (report.artifactResults === undefined || report.artifactResults.length === 0)) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['artifactResults'],
            message: 'Integrity decisions require per-artifact evidence',
        });
    }
    if (report.integrityVerified !== undefined &&
        report.artifactResults?.some((result) => result.integrityVerified !== report.integrityVerified)) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['integrityVerified'],
            message: 'Aggregate integrity must match every artifact result',
        });
    }
});
exports.ProvisionRolloutStateAck = zod_1.z
    .object({
    reportId: zod_1.z.string().min(1),
    rolloutTargetId: zod_1.z.string().min(1),
    state: exports.ProvisionUpdateState,
    duplicate: zod_1.z.boolean(),
    receivedAt: zod_1.z.string().datetime(),
})
    .strict();
exports.ProvisionObservedDeviceHealth = zod_1.z
    .object({
    deviceId: zod_1.z.string().min(1),
    state: zod_1.z.enum(['unknown', 'healthy', 'degraded', 'failed']),
    diagnosticCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable()
        .optional(),
})
    .strict();
exports.ProvisionObservedStateReport = zod_1.z
    .object({
    coreArtifactId: zod_1.z.string().min(1).nullable().optional(),
    coreVersion: zod_1.z.string().trim().min(1).max(80),
    coreChecksum: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable()
        .optional(),
    uiArtifactId: zod_1.z.string().min(1).nullable().optional(),
    uiVersion: zod_1.z.string().trim().max(80).nullable().optional(),
    uiChecksum: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable()
        .optional(),
    uiPortVersion: zod_1.z.string().trim().max(80).nullable().optional(),
    stagedCoreArtifactId: zod_1.z.string().min(1).nullable().optional(),
    stagedUiArtifactId: zod_1.z.string().min(1).nullable().optional(),
    activeRevision: zod_1.z.number().int().min(1).nullable().optional(),
    activeChecksum: zod_1.z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable()
        .optional(),
    stagedRevision: zod_1.z.number().int().min(1).nullable().optional(),
    updateState: exports.ProvisionUpdateState,
    lastErrorCode: zod_1.z
        .string()
        .regex(/^[A-Z0-9_.-]+$/)
        .max(120)
        .nullable()
        .optional(),
    deviceHealth: zod_1.z.array(exports.ProvisionObservedDeviceHealth).max(128).default([]),
    reportedAt: zod_1.z.string().datetime(),
})
    .strict();
/**
 * How an application instance belongs to the calling terminal (FN-154).
 *
 * `terminal`: the instance is enrolled for this terminal itself
 * (`legacyTerminalId`) — the one the legacy bridge creates at registration.
 * `endpoint`: the instance sits on the physical endpoint this terminal is
 * bound to. Both pass the same ownership rule the terminal-facing
 * `applications/:applicationId/*` routes apply.
 */
exports.ProvisionTerminalApplicationBinding = zod_1.z.enum(['terminal', 'endpoint']);
exports.ProvisionTerminalApplication = zod_1.z
    .object({
    applicationInstanceId: zod_1.z.string().min(1),
    endpointId: zod_1.z.string().min(1),
    code: zod_1.z.string().min(1),
    intrinsicType: terminal_js_1.TerminalKind,
    binding: exports.ProvisionTerminalApplicationBinding,
})
    .strict();
/**
 * `GET /provision/applications/current` (FN-154): the application
 * instance(s) BOS has enrolled for the calling terminal, so no client has to
 * construct an application instance id itself.
 *
 * Only active instances are listed, and never an empty list — a terminal with
 * none gets 404 `application_not_enrolled` instead. The terminal's own
 * enrolment (`binding: 'terminal'`) comes first; there is at most one.
 */
exports.ProvisionTerminalApplications = zod_1.z
    .object({
    terminalId: zod_1.z.string().min(1),
    applications: zod_1.z.array(exports.ProvisionTerminalApplication).min(1),
})
    .strict();
/**
 * RL-287: paged Provision content.
 *
 * `GET /provision/applications/:id/content-payload` answers with ONE response
 * and refuses a large catalogue with 409 `content_payload_too_large` (RL-441).
 * That endpoint is unchanged. Paged mode is the additive alternative:
 *
 * 1. `GET /provision/applications/:id/content-manifest` returns the small,
 *    bounded collections (promotions, categories, hotkeys, country pack) and a
 *    `contentToken` that binds a walk to one desired-state revision and one
 *    price scope.
 * 2. `GET /provision/applications/:id/content-pages?token=…[&cursor=…][&limit=…]`
 *    returns one page of the assortment: items with their barcodes, plus the
 *    prices of exactly those items. Follow `nextCursor` until `hasMore` is false.
 *
 * Pages are keyset pages over the item id, so the same cursor over the same
 * data returns the same page, and a walk can resume from any cursor it was
 * given. Row counts per page are capped server-side; BOS ends a page early
 * (fewer items than `limit`) rather than exceed a cap.
 */
exports.PROVISION_CONTENT_PAGED_CONTRACT = 'provision-content-paged/v1';
exports.PROVISION_CONTENT_PAGE_DEFAULT_LIMIT = 1000;
/** Upper bound for `limit`; it is also the most item ids one page query binds. */
exports.PROVISION_CONTENT_PAGE_MAX_LIMIT = 2000;
/** Most price rows one page carries. */
exports.PROVISION_CONTENT_PAGE_MAX_PRICES = 10_000;
/** Most barcode rows one page carries. */
exports.PROVISION_CONTENT_PAGE_MAX_BARCODES = 10_000;
const ContentToken = zod_1.z.string().regex(/^[a-f0-9]{64}$/);
exports.ProvisionContentPageQuery = zod_1.z
    .object({
    token: ContentToken,
    /** Opaque; absent or empty means the first page. */
    cursor: zod_1.z.string().max(512).optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(exports.PROVISION_CONTENT_PAGE_MAX_LIMIT).optional(),
})
    .strict();
exports.ProvisionContentManifest = zod_1.z
    .object({
    contract: zod_1.z.literal(exports.PROVISION_CONTENT_PAGED_CONTRACT),
    revisionChecksum: zod_1.z.string().min(1),
    contentToken: ContentToken,
    assortmentMode: zod_1.z.enum(['all_active', 'selected']),
    /** Resolved department or sole-active fallback price list; null = no active fallback list. */
    priceListId: zod_1.z.string().min(1).nullable(),
    page: zod_1.z
        .object({
        defaultLimit: zod_1.z.number().int().positive(),
        maxLimit: zod_1.z.number().int().positive(),
        maxPrices: zod_1.z.number().int().positive(),
        maxBarcodes: zod_1.z.number().int().positive(),
    })
        .strict(),
    promotions: zod_1.z.array(zod_1.z.record(zod_1.z.string(), zod_1.z.unknown())),
    categories: zod_1.z.array(zod_1.z.record(zod_1.z.string(), zod_1.z.unknown())),
    hotkeys: zod_1.z.array(zod_1.z.unknown()),
    countryPack: zod_1.z.unknown(),
})
    .strict();
exports.ProvisionContentPageItem = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    barcodes: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string().min(1), itemId: zod_1.z.string().min(1) }).passthrough()),
})
    .passthrough();
/** Money crosses the wire as exact minor-unit decimal strings, never floats. */
exports.ProvisionContentPagePrice = zod_1.z
    .object({
    id: zod_1.z.string().min(1),
    itemId: zod_1.z.string().min(1),
    priceListId: zod_1.z.string().min(1),
    unitPriceMinor: zod_1.z.string().regex(/^-?\d+$/),
})
    .passthrough();
exports.ProvisionContentPage = zod_1.z
    .object({
    contentToken: ContentToken,
    items: zod_1.z.array(exports.ProvisionContentPageItem).max(exports.PROVISION_CONTENT_PAGE_MAX_LIMIT),
    prices: zod_1.z.array(exports.ProvisionContentPagePrice).max(exports.PROVISION_CONTENT_PAGE_MAX_PRICES),
    nextCursor: zod_1.z.string().min(1).nullable(),
    hasMore: zod_1.z.boolean(),
})
    .strict()
    .superRefine((page, context) => {
    if (page.hasMore !== (page.nextCursor !== null)) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'nextCursor is present exactly when hasMore is true',
            path: ['nextCursor'],
        });
    }
    const itemIds = new Set(page.items.map((item) => item.id));
    if (page.prices.some((price) => !itemIds.has(price.itemId))) {
        context.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'a page carries prices only for its own items',
            path: ['prices'],
        });
    }
});
//# sourceMappingURL=provision.js.map